import { debounce, throttle, prefersReducedMotion } from '@theme/utilities';

/**
 * Timeout duration after which scrolling is considered finished.
 * @constant {number}
 */
const SCROLL_END_TIMEOUT = 50;

export class Scroller {
  #animationFrame = null;
  #animationCleanup = null;
  #animating = false;

  /** @type {HTMLElement} */
  element;

  /** @type {Promise<void>|undefined} */
  #promise = undefined;

  /** @type {Function|undefined} */
  #resolve = undefined;

  /** @type {() => void} */
  #throttledCallback;

  /** @type {() => void} */
  #endCallback;

  /** @type {(() => void) | undefined} */
  #onScrollInit;

  /** @type {(() => void) | undefined} */
  #onScrollEnd;

  #userEvent = true;
  #ignore = false;
  #isScrolling = false;

  /**
   * @param {HTMLElement} element
   * @param {Object} options
   * @param {() => void} options.onScroll
   * @param {() => void} [options.onScrollStart]
   * @param {() => void} [options.onScrollEnd]
   */
  constructor(element, options) {
    this.#throttledCallback = throttle(
      options.onScroll,
      SCROLL_END_TIMEOUT
    );
    this.#endCallback = options.onScroll;
    this.#onScrollInit = options.onScrollStart;
    this.#onScrollEnd = options.onScrollEnd;

    this.element = element;
    this.element.addEventListener('scroll', this.#handleScroll);
  }

  /**
   * @param {number | HTMLElement} input
   * @param {Object} [options]
   * @param {boolean} [options.instant]
   */
  async to(input, options) {
    let value;

    if (input instanceof HTMLElement) {
      const paddingStart = calculatePaddingStart(
        this.element,
        this.axis
      );
      value = input[`offset${this.#edge}`] - paddingStart;
    } else {
      value = input;
    }

    const currentPosition = this.element[`scroll${this.#edge}`];
    const willChange = currentPosition !== value;

    if (willChange) {
      this.#scroll({ ...options, method: 'scrollTo', value });
    } else if (this.#isScrolling) {
      this.#handleScrollEnd(false);
    }
  }

  /**
   * @param {number} value
   * @param {Object} [options]
   * @param {boolean} [options.instant]
   */
  by(value, options) {
    this.#scroll({ ...options, method: 'scrollBy', value });
  }

  /**
   * @param {Object} options
   * @param {'scrollTo' | 'scrollBy'} options.method
   * @param {number} options.value
   * @param {boolean} [options.instant]
   */
  #scroll(options) {
    const {
      method,
      value,
      instant = prefersReducedMotion(),
    } = options;

    this.#cancelAnimation();
    this.#reset();
    this.#ignore = instant;
    this.#userEvent = false;

    const edge = this.#edge;
    const currentPosition = this.element[`scroll${edge}`];
    const targetPosition =
      method === 'scrollBy'
        ? currentPosition + value
        : value;

    if (Math.abs(targetPosition - currentPosition) < 1) return;

    const banner = this.element.closest(
      '.slideshow-section slideshow-component[autoplay]'
    );

    if (!instant && !prefersReducedMotion() && banner) {
      // Duration of the banner transition: 1000 ms = 1 second.
      const duration = 1000;
      const startTime = performance.now();

      const oldSnap = this.element.style.getPropertyValue(
        'scroll-snap-type'
      );
      const oldSnapPriority =
        this.element.style.getPropertyPriority('scroll-snap-type');

      const oldBehavior = this.element.style.getPropertyValue(
        'scroll-behavior'
      );
      const oldBehaviorPriority =
        this.element.style.getPropertyPriority('scroll-behavior');

      const maxPosition =
        edge === 'Left'
          ? this.element.scrollWidth - this.element.clientWidth
          : this.element.scrollHeight - this.element.clientHeight;

      const rtl =
        edge === 'Left' &&
        getComputedStyle(this.element).direction === 'rtl';

      const target = rtl
        ? Math.max(-maxPosition, Math.min(0, targetPosition))
        : Math.max(0, Math.min(maxPosition, targetPosition));

      this.element.style.setProperty(
        'scroll-snap-type',
        'none',
        'important'
      );
      this.element.style.setProperty(
        'scroll-behavior',
        'auto',
        'important'
      );

      this.#animationCleanup = () => {
        if (oldSnap) {
          this.element.style.setProperty(
            'scroll-snap-type',
            oldSnap,
            oldSnapPriority
          );
        } else {
          this.element.style.removeProperty('scroll-snap-type');
        }

        if (oldBehavior) {
          this.element.style.setProperty(
            'scroll-behavior',
            oldBehavior,
            oldBehaviorPriority
          );
        } else {
          this.element.style.removeProperty('scroll-behavior');
        }
      };

      this.#animating = true;
      this.#setup();

      const animate = (now) => {
        const progress = Math.min(
          (now - startTime) / duration,
          1
        );
        const eased =
          (1 - Math.cos(Math.PI * progress)) / 2;

        this.element[`scroll${edge}`] =
          currentPosition +
          (target - currentPosition) * eased;

        if (progress < 1) {
          this.#animationFrame =
            requestAnimationFrame(animate);
          return;
        }

        this.#animationFrame = null;
        this.#animating = false;
        this.#animationCleanup?.();
        this.#animationCleanup = null;
        this.#handleScrollEnd(false);
      };

      this.#animationFrame = requestAnimationFrame(animate);
      return;
    }

    if (!instant) this.#setup();

    this.element[method]({
      [edge.toLowerCase()]: value,
      behavior: instant ? 'instant' : 'smooth',
    });
  }

  #cancelAnimation() {
    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
    }

    this.#animationFrame = null;
    this.#animating = false;
    this.#animationCleanup?.();
    this.#animationCleanup = null;
  }

  /** @returns {'x' | 'y'} */
  get axis() {
    return getScrollAxis(this.element);
  }

  /** @returns {Promise<void>} */
  get finished() {
    return this.#promise ?? Promise.resolve();
  }

  /** @returns {'Left' | 'Top'} */
  get #edge() {
    return this.axis === 'x' ? 'Left' : 'Top';
  }

  #setup() {
    if (this.#promise) return;

    this.#promise = new Promise(
      (resolve) => (this.#resolve = resolve)
    );
  }

  #reset = () => {
    this.#handleScrollEnd.cancel();
    this.#resolve?.();

    this.#promise = undefined;
    this.#resolve = undefined;
    this.#userEvent = true;
    this.#ignore = false;
  };

  #handleScroll = () => {
    if (!this.#isScrolling) {
      this.#onScrollInit?.();
      this.#isScrolling = true;
    }

    if (this.#animating) {
      this.#handleScrollEnd.cancel();
      return;
    }

    if (this.#ignore) {
      this.#reset();
      this.#handleScrollEnd(false);
      return;
    }

    const userEvent = this.#userEvent;

    this.#setup();

    if (userEvent) this.#throttledCallback();

    this.#handleScrollEnd(userEvent);
  };

  #handleScrollEnd = debounce(
    /**
     * @param {boolean} userEvent
     */
    (userEvent) => {
      this.#resolve?.();

      if (userEvent) this.#endCallback();

      this.#reset();

      if (this.#isScrolling) {
        this.#onScrollEnd?.();
        this.#isScrolling = false;
      }
    },
    SCROLL_END_TIMEOUT
  );

  /**
   * @param {boolean} value
   */
  set snap(value) {
    this.#ignore = true;
    this.element.style.setProperty(
      'scroll-snap-type',
      value ? `${this.axis} mandatory` : 'none'
    );
  }

  destroy() {
    this.#cancelAnimation();
    this.#reset();
    this.element.removeEventListener(
      'scroll',
      this.#handleScroll
    );
  }
}

/**
 * @param {HTMLElement} el
 * @returns {'x' | 'y'}
 */
function getScrollAxis(el) {
  if (
    el.scrollHeight > el.clientHeight &&
    el.scrollWidth === el.clientWidth
  ) {
    return 'y';
  }

  if (
    el.scrollWidth > el.clientWidth &&
    el.scrollHeight === el.clientHeight
  ) {
    return 'x';
  }

  return el.scrollWidth > el.scrollHeight ? 'x' : 'y';
}

/**
 * @param {HTMLElement} element
 * @param {'x' | 'y'} axis
 * @returns {number}
 */
function calculatePaddingStart(element, axis) {
  const computedStyle = getComputedStyle(element);
  const value =
    axis === 'x'
      ? computedStyle.paddingInlineStart
      : computedStyle.paddingBlockStart;

  return parseFloat(value);
}

/**
 * @param {Element} element
 * @param {Object} options
 * @param {ScrollBehavior} [options.behavior='smooth']
 * @param {'start' | 'center' | 'end'} [options.block='start']
 * @param {'start' | 'center' | 'end'} [options.inline='start']
 * @param {Element} [options.ancestor]
 */
export function scrollIntoView(
  element,
  {
    ancestor,
    behavior = 'smooth',
    block = 'start',
    inline = 'start',
  } = {}
) {
  if (!ancestor) {
    return element.scrollIntoView({
      behavior,
      block,
      inline,
    });
  }

  const elemRect = element.getBoundingClientRect();
  const ancestorRect = ancestor.getBoundingClientRect();

  /**
   * @param {'start' | 'center' | 'end'} alignment
   * @param {number} ancestorStart
   * @param {number} ancestorLength
   * @param {number} elemStart
   * @param {number} elemLength
   * @param {number} currentScroll
   * @returns {number}
   */
  const calculateScrollOffset = (
    alignment,
    ancestorStart,
    ancestorLength,
    elemStart,
    elemLength,
    currentScroll
  ) => {
    switch (alignment) {
      case 'start':
        return currentScroll + elemStart - ancestorStart;

      case 'center':
        return (
          currentScroll +
          elemStart -
          ancestorStart -
          ancestorLength / 2 +
          elemLength / 2
        );

      case 'end':
        return (
          currentScroll +
          elemStart -
          ancestorStart -
          ancestorLength +
          elemLength
        );

      default:
        return currentScroll;
    }
  };

  const scrollTop =
    ancestor.scrollHeight > ancestor.clientHeight
      ? calculateScrollOffset(
          block,
          ancestorRect.top,
          ancestor.clientHeight,
          elemRect.top,
          elemRect.height,
          ancestor.scrollTop
        )
      : ancestor.scrollTop;

  const scrollLeft =
    ancestor.scrollWidth > ancestor.clientWidth
      ? calculateScrollOffset(
          inline,
          ancestorRect.left,
          ancestor.clientWidth,
          elemRect.left,
          elemRect.width,
          ancestor.scrollLeft
        )
      : ancestor.scrollLeft;

  ancestor.scrollTo({
    top: scrollTop,
    left: scrollLeft,
    behavior,
  });
}

class ScrollHint extends HTMLElement {
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    this.addEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const {
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollLeft,
      scrollWidth,
      clientWidth,
    } = this;

    const scrollDirection =
      scrollWidth > clientWidth
        ? 'horizontal'
        : 'vertical';

    const scrollPercentage =
      scrollDirection === 'vertical'
        ? scrollTop / (scrollHeight - clientHeight)
        : scrollLeft / (scrollWidth - clientWidth);

    this.style.maskImage = Number.isNaN(scrollPercentage)
      ? ''
      : `linear-gradient(
        to ${scrollDirection === 'vertical' ? 'bottom' : 'right'},
        transparent ${scrollPercentage > 0 ? 1 : 0}%,
        black ${scrollPercentage < 0.1 ? scrollPercentage * 100 : 10}%,
        black ${scrollPercentage > 0.9 ? scrollPercentage * 100 : 90}%,
        transparent 100%
      )`;
  };

  #resizeObserver = new ResizeObserver(this.#update);
}

if (!customElements.get('scroll-hint')) {
  customElements.define('scroll-hint', ScrollHint);
}